import type { MenuProps } from 'antd'

import { Icon } from 'Shared/atoms'

import { MenuKey as CoreMenuKey } from 'Core/constants'
import { handleLogout } from 'Core/utils/auth'
import { generateLabel } from 'Core/utils/generateLabel'

import { BANNER_VISIBILITY_KEY } from 'Portals/Customer/Pages/Dashboard/common/entities/Banner/utils'
import i18n from 'Portals/Customer/utils/i18nService'

import { RouteBuilder } from '../../utils'
import { AuthInstance } from '../auth'

import { MenuKey } from './constants'

type ItemType = NonNullable<MenuProps['items']>[number]

export const getMenuItems = ({
	isImpersonatedMode,
	isEhsEnabled
}: {
	isImpersonatedMode?: boolean
	isEhsEnabled?: boolean
}): ItemType[] =>
	[
		{
			key: MenuKey.DASHBOARD,
			icon: <Icon type="dashboard" />,
			label: generateLabel({
				name: i18n.t('menuItems:dashboard'),
				path: RouteBuilder.dashboard.build()
			})
		},
		{
			key: MenuKey.PURCHASE_ORDER,
			icon: <Icon type="purchaseOrder" />,
			label: generateLabel({
				name: i18n.t('menuItems:purchaseOrders'),
				path: RouteBuilder.purchaseOrder.build()
			})
		},
		{
			key: MenuKey.SERVICE_LOCATION,
			icon: <Icon type="customers" />,
			label: generateLabel({
				name: i18n.t('menuItems:serviceLocation'),
				path: RouteBuilder.serviceLocation.build()
			})
		},
		{
			key: MenuKey.EHS_DATA,
			icon: <Icon type="graphic" />,
			label: i18n.t('menuItems:ehsData'),
			isHidden: !isEhsEnabled,
			children: [
				{
					key: MenuKey.LOCATION,
					label: generateLabel({
						name: i18n.t('menuItems:location'),
						path: RouteBuilder.location.build()
					})
				},
				{
					key: MenuKey.METRIC_BY_PERIODS,
					label: generateLabel({
						name: i18n.t('menuItems:metrics'),
						path: RouteBuilder.metricByPeriods.build()
					})
				},
				{
					key: MenuKey.AUDIT,
					label: generateLabel({
						name: i18n.t('menuItems:audit'),
						path: RouteBuilder.audit.build()
					})
				}
			]
		},
		{
			key: MenuKey.ANALYTICS,
			icon: <Icon type="analytic" />,
			label: generateLabel({
				name: i18n.t('menuItems:analytics'),
				path: RouteBuilder.analytic.build()
			})
		},
		// make Orders List page hidden in scope of Lift&Shift phase
		// at https://heritage-enviro.atlassian.net/browse/IS2-11815
		/*{
		key: MenuKey.ORDERS,
		icon: <Icon type="orderList" />,
		label: generateLabel({ name: i18n.t('menuItems:ordersList'), path: RouteBuilder.order.build() })
	},*/
		{
			key: MenuKey.REPORT,
			icon: <Icon type="report" />,
			label: generateLabel({
				name: i18n.t('menuItems:reports'),
				path: RouteBuilder.report.build()
			})
		},
		{
			key: MenuKey.REFRENCE_DOCS,
			icon: <Icon type="copy" />,
			label: i18n.t('menuItems:referenceDocs'),
			children: [
				{
					key: MenuKey.COMPANY_CATEGORIES,
					label: generateLabel({
						name: i18n.t('menuItems:companyCategories'),
						path: RouteBuilder.companyCategory.build()
					})
				},
				{
					key: MenuKey.ACCOUNT_CATEGORIES,
					label: generateLabel({
						name: i18n.t('menuItems:accountCategories'),
						path: RouteBuilder.accountCategory.build()
					})
				},
				{
					key: MenuKey.TREATMENT_CATEGORIES,
					label: generateLabel({
						name: i18n.t('menuItems:treatmentCategories'),
						path: RouteBuilder.treatmentCategory.build()
					})
				},
				{
					key: MenuKey.TREATMENT_METHODS,
					label: generateLabel({
						name: i18n.t('menuItems:treatmentMethods'),
						path: RouteBuilder.treatmentMethod.build()
					})
				}
			]
		},
		{
			key: MenuKey.RESOURCES,
			icon: <Icon type="item" />,
			label: i18n.t('menuItems:resources'),
			children: [
				{
					key: MenuKey.WASTESTREAM,
					label: generateLabel({
						name: i18n.t('menuItems:wastestreams'),
						path: RouteBuilder.wastestream.build()
					})
				},
				{
					key: MenuKey.MANIFESTS,
					label: generateLabel({
						name: i18n.t('menuItems:manifests'),
						path: RouteBuilder.manifest.build()
					})
				},
				{
					key: MenuKey.BIOHAZARDUS_BOL,
					label: generateLabel({
						name: i18n.t('menuItems:biohazardousBOLs'),
						path: RouteBuilder.biohazardousManifest.build()
					})
				},
				{
					key: MenuKey.BACKUP_DOCUMENT,
					label: generateLabel({
						name: i18n.t('menuItems:backupDocuments'),
						path: RouteBuilder.backupDocument.build()
					})
				},
				{
					key: MenuKey.MOUs,
					label: generateLabel({
						name: i18n.t('menuItems:MOUs'),
						path: RouteBuilder.mou.build()
					})
				},
				{
					key: MenuKey.SITE_DOCUMENT,
					label: generateLabel({
						name: i18n.t('menuItems:siteDocuments'),
						path: RouteBuilder.siteDocument.build()
					})
				},
				{
					key: MenuKey.CUSTOMER_INVOICE,
					label: generateLabel({
						name: i18n.t('menuItems:customerInvoices'),
						path: RouteBuilder.customerInvoice.build()
					})
				}
			]
		},
		{
			type: 'divider'
		},
		{
			key: MenuKey.PROFILE,
			icon: <Icon type="profile" />,
			label: generateLabel({
				name: i18n.t('menuItems:profile'),
				path: RouteBuilder.profile.build()
			})
		},
		{
			key: MenuKey.HELP_CENTER,
			icon: <Icon type="helpCenter" />,
			label: generateLabel({
				name: i18n.t('menuItems:helpCenter'),
				path: RouteBuilder.helpCenter.build()
			})
		},
		{
			key: CoreMenuKey.LOGOUT,
			icon: <Icon type="logout" />,
			label: generateLabel({
				name: i18n.t('menuItems:logout'),
				onClick: () => {
					sessionStorage.removeItem(BANNER_VISIBILITY_KEY)
					handleLogout({
						isImpersonatedMode,
						signOutRedirect: AuthInstance.signOutRedirect
					})
				},
				isLogout: true
			})
		}
	]
		.filter(({ isHidden }) => !isHidden)
		.map(({ isHidden, ...item }) => item as ItemType)
